import React, { useEffect, useState } from "react";
import axios from "../../../utils/axiosInstance";
import { ChartBar, Lock, LockOpen, EyeSlash } from "@phosphor-icons/react";

export const PollCard = ({ message, isAdmin, onUpdated }) => {
  const poll = message.poll || {};
  const mine = (poll.options || []).filter((o) => o.voted).map((o) => o.id);
  const [sel, setSel] = useState(mine);
  const [busy, setBusy] = useState(false);
  const [showVoters, setShowVoters] = useState(false);
  const mineKey = mine.join(",");
  useEffect(() => { setSel(mineKey ? mineKey.split(",") : []); }, [mineKey]);

  const hasVoted = mine.length > 0;
  const total = poll.total_voters || 0;
  const changed = sel.slice().sort().join(",") !== mine.slice().sort().join(",");

  const toggle = (id) => {
    if (poll.closed) return;
    setSel((p) => (poll.multiple ? (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]) : [id]));
  };
  const call = async (fn) => {
    setBusy(true);
    try { const r = await fn(); onUpdated(r.data); } catch (e) { alert("Błąd: " + (e.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };
  const vote = () => call(() => axios.post(`/api/chat/messages/${message.id}/vote`, { option_ids: sel }));
  const closeToggle = () => call(() => axios.post(`/api/chat/messages/${message.id}/close-poll`));

  return (
    <div className="bg-white text-foreground border border-border rounded-md p-3 w-[260px] sm:w-[340px] max-w-full" data-testid={`poll-${message.id}`}>
      <div className="flex items-start gap-2 mb-1">
        <ChartBar className="w-4 h-4 text-[#2563EB] mt-0.5 shrink-0" weight="bold" />
        <div className="font-semibold text-sm flex-1 break-words">{poll.question}</div>
      </div>
      <div className="flex flex-wrap gap-1.5 mb-2 text-[10px] font-semibold">
        <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{poll.multiple ? "Wielokrotny wybór" : "Jeden wybór"}</span>
        {poll.anonymous && <span className="px-1.5 py-0.5 rounded bg-violet-50 text-violet-700 inline-flex items-center gap-1"><EyeSlash className="w-3 h-3" /> Anonimowa</span>}
        {poll.closed && <span className="px-1.5 py-0.5 rounded bg-red-50 text-red-700 inline-flex items-center gap-1"><Lock className="w-3 h-3" /> Zamknięta</span>}
      </div>
      <div className="space-y-1.5">
        {(poll.options || []).map((o) => {
          const pct = total ? Math.round((o.count / total) * 100) : 0;
          const checked = sel.includes(o.id);
          return (
            <div key={o.id}>
              <button
                onClick={() => toggle(o.id)}
                disabled={poll.closed || busy}
                className={`relative w-full text-left rounded-md border px-2.5 py-1.5 overflow-hidden transition-colors ${checked ? "border-[#2563EB]" : "border-border hover:border-slate-400"} disabled:cursor-default`}
                data-testid={`poll-option-${o.id}`}
              >
                <span className="absolute inset-y-0 left-0 bg-blue-100 transition-[width] duration-500" style={{ width: `${pct}%` }} />
                <span className="relative flex items-center gap-2 text-sm">
                  <span className={`w-4 h-4 shrink-0 border-2 flex items-center justify-center ${poll.multiple ? "rounded-sm" : "rounded-full"} ${checked ? "border-[#2563EB] bg-[#2563EB]" : "border-slate-400"}`}>
                    {checked && <span className={`w-1.5 h-1.5 bg-white ${poll.multiple ? "rounded-[1px]" : "rounded-full"}`} />}
                  </span>
                  <span className="flex-1 break-words">{o.text}</span>
                  <span className="text-xs font-semibold text-slate-600" data-testid={`poll-count-${o.id}`}>{o.count} · {pct}%</span>
                </span>
              </button>
              {showVoters && o.voters && o.voters.length > 0 && (
                <div className="text-[11px] text-slate-500 pl-7 pt-0.5" data-testid={`poll-voters-${o.id}`}>{o.voters.join(", ")}</div>
              )}
            </div>
          );
        })}
      </div>
      <div className="flex items-center justify-between gap-2 mt-2 flex-wrap">
        <span className="text-xs text-slate-500" data-testid={`poll-total-${message.id}`}>Głosów: {total}</span>
        <div className="flex items-center gap-1.5 flex-wrap justify-end">
          {total > 0 && (poll.options || []).some((o) => o.voters) && (
            <button onClick={() => setShowVoters((v) => !v)} className="text-xs text-[#2563EB] hover:underline whitespace-nowrap" data-testid={`poll-show-voters-${message.id}`}>
              {showVoters ? "Ukryj kto głosował" : "Kto głosował"}
            </button>
          )}
          {isAdmin && (
            <button onClick={closeToggle} disabled={busy} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-slate-100 hover:bg-slate-200" data-testid={`poll-close-${message.id}`}>
              {poll.closed ? <><LockOpen className="w-3 h-3" /> Otwórz</> : <><Lock className="w-3 h-3" /> Zamknij</>}
            </button>
          )}
          {!poll.closed && (changed || !hasVoted) && (
            <button onClick={vote} disabled={busy || (sel.length === 0 && !hasVoted)} className="text-xs px-3 py-1 rounded-md bg-[#2563EB] text-white font-semibold disabled:opacity-50" data-testid={`poll-vote-${message.id}`}>
              {hasVoted ? (sel.length ? "Zmień głos" : "Wycofaj głos") : "Głosuj"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
